// Command palette bootstrap entries.
//
// Hand-rolled mirror of specs/seeds/dashboard-v2/command-palette.yaml.
// Shape parity is enforced by __tests__/commands-spec-parity.test.ts so this
// literal cannot drift from the YAML undetected.
//
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md
// (the v2 FE generator does not emit palette commands yet; this slice lands
// the surface, generator wiring is a follow-up).

import type { CommandEntry } from "./registry";

// spec: specs/seeds/dashboard-v2/command-palette.yaml
export const defaultCommands: CommandEntry[] = [
  // ─── Pages ────────────────────────────────────────────────────────
  {
    id: "go-overview",
    label: "Overview",
    group: "pages",
    route: "/",
    icon: "layout-dashboard",
    keywords: ["home", "dashboard", "start"],
  },
  {
    id: "go-donors",
    label: "Donors",
    group: "pages",
    route: "/donors",
    icon: "users",
    forwardPromised: true,
    keywords: ["people", "supporters"],
  },
  {
    id: "go-campaigns",
    label: "Campaigns",
    group: "pages",
    route: "/campaigns",
    icon: "megaphone",
    forwardPromised: true,
    keywords: ["fundraisers", "appeals"],
  },
  {
    id: "go-designations",
    label: "Designations",
    group: "pages",
    route: "/designations",
    icon: "folder-tree",
    forwardPromised: true,
    keywords: ["funds", "programs", "projects", "causes"],
  },
  {
    id: "go-donations",
    label: "Donations",
    group: "pages",
    route: "/donations",
    icon: "hand-coins",
    forwardPromised: true,
    keywords: ["gifts", "transactions", "receipts"],
  },
  {
    id: "go-pledges",
    label: "Pledges",
    group: "pages",
    route: "/pledges",
    icon: "handshake",
    forwardPromised: true,
    keywords: ["promises", "commitments"],
  },
  {
    id: "go-recurring",
    label: "Recurring giving",
    group: "pages",
    route: "/recurring",
    icon: "repeat",
    forwardPromised: true,
    keywords: ["subscriptions", "monthly", "sustainers"],
  },
  {
    id: "go-reports",
    label: "Reports & analytics",
    group: "pages",
    route: "/reports",
    icon: "bar-chart-3",
    forwardPromised: true,
    keywords: ["insights", "charts", "analytics"],
  },
  {
    id: "go-messaging",
    label: "Messaging",
    group: "pages",
    route: "/messaging",
    icon: "message-square",
    forwardPromised: true,
    keywords: ["communications", "email", "sms", "broadcast"],
  },
  {
    id: "go-channels",
    label: "Channels",
    group: "pages",
    route: "/channels",
    icon: "layers",
    forwardPromised: true,
    keywords: ["donate pages", "widgets", "kiosks", "signage"],
  },
  {
    id: "go-automation",
    label: "Automation",
    group: "pages",
    route: "/automation",
    icon: "workflow",
    forwardPromised: true,
    keywords: ["lifecycles", "journeys", "rules"],
  },
  {
    id: "go-people",
    label: "People & access",
    group: "pages",
    route: "/people",
    icon: "user-cog",
    forwardPromised: true,
    keywords: ["team", "members", "roles", "groups"],
  },
  {
    id: "go-settings",
    label: "Settings",
    group: "pages",
    route: "/settings",
    icon: "settings",
    forwardPromised: true,
    keywords: ["config", "preferences", "organization"],
  },
  // ─── Actions ──────────────────────────────────────────────────────
  {
    id: "action-record-donation",
    label: "Record donation",
    group: "actions",
    icon: "plus-circle",
    forwardPromised: true,
    keywords: ["new gift", "manual donation", "give", "log donation"],
  },
  {
    id: "action-create-campaign",
    label: "Create campaign",
    group: "actions",
    icon: "plus-circle",
    forwardPromised: true,
    keywords: ["new campaign", "start campaign", "fundraiser"],
  },
  {
    id: "action-create-designation",
    label: "Create designation",
    group: "actions",
    icon: "plus-circle",
    forwardPromised: true,
    keywords: ["new fund", "new program", "new project", "new cause"],
  },
  {
    id: "action-record-pledge",
    label: "Record pledge",
    group: "actions",
    icon: "plus-circle",
    forwardPromised: true,
    keywords: ["new pledge", "commitment"],
  },
  {
    id: "action-import-donations",
    label: "Import donations",
    group: "actions",
    icon: "upload",
    forwardPromised: true,
    keywords: ["csv", "upload", "bulk import", "ingest"],
  },
  {
    id: "action-run-reconciliation",
    label: "Run reconciliation",
    group: "actions",
    icon: "refresh-cw",
    forwardPromised: true,
    keywords: ["reconcile", "match", "payouts"],
  },
  {
    id: "action-send-broadcast",
    label: "Send broadcast",
    group: "actions",
    icon: "send",
    forwardPromised: true,
    keywords: ["email blast", "message all", "announcement"],
  },
  {
    id: "action-invite-member",
    label: "Invite member",
    group: "actions",
    icon: "user-plus",
    forwardPromised: true,
    keywords: ["add user", "team invite", "staff"],
  },
  {
    id: "action-open-donate-page-builder",
    label: "Open donate-page builder",
    group: "actions",
    icon: "pen-tool",
    forwardPromised: true,
    keywords: ["design page", "edit donate page", "builder"],
  },
  // CHG-1662 / ITER-3771 — donor + campaign "sample" entries (6 rows
  // total: Alice Park / Brian Okafor / Carmen Diaz / Spring Appeal /
  // Building Fund 2026 / Year-end Match) were removed. Field-test v3
  // observed them leaking into donor-search results inside the
  // palette, which is misleading — they were never real records.
  // Live donor search is wired in CommandPalette.tsx via the
  // donorsApi.listDonors fetcher (kicks in once the query is ≥2
  // chars). Live campaign search remains a forward-promise; the
  // palette returns no campaign rows for arbitrary queries today.
];
